import React, { useEffect, useRef, useMemo } from "react";
import * as R from "ramda";
import { connect } from "react-redux";
import { compose, withStateHandlers } from "recompose";
import {
  Avatar,
  Row,
  Spin,
  Button as AntButton,
  Popconfirm,
  notification
} from "antd";
import { Content } from "@worcket/common/components/layout";
import { Form, FormItem } from "@worcket/common/components/controls";
import { withForm, isLoading } from "@worcket/common/hocs";
import { useTranslation } from "react-i18next";

import BrowserLayout from "../../../../../components/BrowserLayout";
import * as gtag from "../../../../../lib/gtag";
import Upload from "../../../../../components/controls/Upload";
import {
  saveOpportunityMedia,
  SAVE_OPPORTUNITY_MEDIA,
  GO_TO_PREVIOUS_WIZARD_STEP
} from "../../../../../store/actions/opportunity";
import { getOptimizedUrl } from "../../../../../utils/videos";
import { getOptimizedImage } from "../../../../../utils/images";
import { FORMS_KEYS } from "../../../../../constants/opportunity";
import Button from "../../../../../components/Button";

import uploadVideoImg from "./img-upload-video.svg";
import uploadPhotoImg from "./img-upload-photo.svg";

import styled from "styled-components/macro";

const Container = styled.div`
  margin-bottom: 40px;
  .media {
    width: 100%;
    height: 300px;

    &.image {
      background: url(${({ src }) => src}) !important;
      background-size: contain !important;
      background-repeat: no-repeat !important;
      background-position: center !important;
      background: black;
      object-fit: cover;
    }

    &.video {
      background: black;
      max-height: 160px;
      border-radius: 5px;
    }
  }
  .ant-upload {
    width: 100%;
  }

  h2 + p {
    margin-bottom: 45px;
  }
`;

const MediaUploadContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 290px;
  border-radius: 5px;
  border-top-left-radius: 0px;
  border-top-right-radius: 0px;
  border: ${({ theme }) => `1px solid ${theme["@gray-40"]}`};
  background-color: ${({ theme }) => theme["@gray-30"]};
  text-align: center;
  font-size: 12px;
  overflow: hidden;

  img {
    margin-top: 5px;
    width: 80px;
    height: 97px;
  }
  strong {
    max-width: 287px;
  }

  .upload-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.9019607843137255);
  }
`;

const VideoUploadContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-radius: 5px;
  border: solid 1px #d0d6db;
  background-color: ${({ theme }) => theme["@gray-20"]};
  text-align: center;
  font-size: 12px;
  height: 160px;

  img {
    margin-top: 10px;
    width: 109px;
    height: 82px;
  }

  strong {
    margin-bottom: 11px;
    max-width: 287px;
  }
  .upload-drag-instruction {
    color: #747d84;
    max-width: 231px;
    margin-bottom: 5px;
  }
  .upload-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.9019607843137255);
    z-index: 1;
  }
`;

const Title = styled.h1`
  margin-left: 10px;
  color: white;
  font-size: 20px;
  text-shadow: 0px 0px 6px #00000029;
  white-space: pre-wrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 57px;
`;

const Layout = styled.div`
  border: ${({ theme }) => `1px solid ${theme["@gray-30"]}`};
  background: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 40px;

  .title {
    margin-left: 15px;
    color: ${({ theme }) => theme["@gray-50"]};
    font-size: 9px;
  }
`;

const Description = styled.p`
  /* Limit to 2 or 3 lines */
  -webkit-line-clamp: ${({ rows }) => rows || 2};
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  display: -webkit-box;
`;

const WrapperButton = styled.div`
  display: flex;
  width: 100%;
  position: absolute;
  top: 20px;
  left: 20px;
`;

const DescriptionUpload = styled.span`
  color: #747d84;
  font-size: 12px;
  width: 250px;
`;

const CompanyName = styled.p`
  text-align: left;
  font-weight: bold;
  margin-top: 20px;
`;

const CompanyDescriptionWrapper = styled.div`
  padding: 10px;
  border: 1px solid #00000026;
  border-radius: 4px;
`;

const Gradient = styled.div`
  position: relative;
  height: 80px;
  top: -84px;
  background: linear-gradient(to top, white, transparent);
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 260px;
  max-width: 260px;
  justify-content: center;

  position: absolute;
  top: 260px;
  right: 10px;
`;

const ContentBottom = styled.div`
  position: absolute;
  top: 234px;
  left: 20px;
  width: 530px;
  max-width: 530px;
`;

const CustomAvatar = styled(Avatar)`
  border-radius: 20px !important;
  margin: auto !important;
  border: ${({ theme }) => `1px solid ${theme["@gray-30"]}`} !important;
  background: white !important;

  img {
    object-fit: cover;
  }
`;

const ContentForm = styled.div`
  padding-left: 159px;
  padding-right: 119px;
`;

const MediaForm = ({
  form,
  video,
  image,
  loading,
  onSubmit,
  setVideo,
  setImage,
  isDisabled,
  newOpportunity,
  goToPreviousStep,
  section,
  opportunity,
  company,
  setForm
}) => {
  const { t } = useTranslation();
  const sectionName = useMemo(() => t("opportunity:media.sectionName"), [t]);
  const ref = useRef(null);
  useEffect(() => {
    if (section && section === sectionName && ref.current) {
      ref.current.scrollIntoView();
    }
  }, [section, sectionName]);

  useEffect(() => {
    setForm && !newOpportunity && setForm(form.getFieldsValue());
  }, [form, newOpportunity, setForm]);

  return (
    <Content>
      <Container ref={ref}>
        <h2>{t("opportunity:media.title")}</h2>
        <p>{t("opportunity:media.subtitle")}</p>

        <Form onSubmit={onSubmit}>
          <ContentForm>
            <BrowserLayout
              url={`${process.env.REACT_APP_APPLY_URL}?id=${R.propOr(
                "id-opportunity",
                "id",
                opportunity
              )}`}
            >
              <div style={{ position: "relative", height: 500 }}>
                <div>
                  <Layout>
                    <span className="title">{company.name}</span>
                  </Layout>
                  <div className="media-item" style={{ position: "relative" }}>
                    <FormItem
                      name="image"
                      form={form}
                      render={
                        <Upload
                          action={process.env.REACT_APP_CLOUDINARY_IMAGE_URL}
                          options={{ upload_preset: "opportunity" }}
                          onChange={setImage}
                          label={t("opportunity:media.selectImage")}
                          disabled={isDisabled}
                          fileType="image/*"
                        >
                          {({ status }) => (
                            <MediaUploadContainer>
                              <WrapperButton>
                                <Button type="quaternary" icon="upload">
                                  {t("opportunity:media.selectImage")}
                                </Button>
                              </WrapperButton>
                              {status === "uploading" && (
                                <div className="upload-loading">
                                  <Spin />
                                </div>
                              )}
                              {image ? (
                                <img
                                  className="media image"
                                  src={getOptimizedImage(image)}
                                  alt={t("opportunity:media.imagePlaceholder")}
                                />
                              ) : (
                                <>
                                  <DescriptionUpload>
                                    {t("opportunity:media.imageUploadDrag")}
                                  </DescriptionUpload>
                                  <img src={uploadPhotoImg} alt="" />
                                </>
                              )}
                            </MediaUploadContainer>
                          )}
                        </Upload>
                      }
                    />
                    {image && (
                      <Popconfirm
                        placement="topLeft"
                        title={t("global:areYouSure")}
                        onConfirm={() => {
                          setImage(null);
                          form.setFieldsValue({
                            image: ""
                          });
                          notification.warning({
                            message: t("global:alert"),
                            description: t("global:rememberSave")
                          });
                        }}
                        okText={t("global:yes")}
                        cancelText={t("global:no")}
                      >
                        <AntButton
                          style={{
                            marginLeft: 10,
                            position: "absolute",
                            top: 21,
                            left: 200
                          }}
                          type="default"
                          icon="delete"
                          sharp="circle"
                        />
                      </Popconfirm>
                    )}
                  </div>
                  <div style={{ display: "flex" }}>
                    <ContentBottom>
                      <div>
                        <Title>{opportunity.title}</Title>
                        <FormItem
                          name="video"
                          form={form}
                          render={
                            <Upload
                              action={
                                process.env.REACT_APP_CLOUDINARY_VIDEO_URL
                              }
                              options={{ upload_preset: "opportunity" }}
                              onChange={setVideo}
                              label={t("opportunity:media.selectVideo")}
                              disabled={isDisabled}
                              fileType="video/*"
                            >
                              {({ status }) => (
                                <VideoUploadContainer>
                                  {status === "uploading" && (
                                    <div className="upload-loading">
                                      <Spin />
                                    </div>
                                  )}
                                  {video ? (
                                    <video
                                      onClick={e => e.stopPropagation()}
                                      className="media video"
                                      controls
                                    >
                                      <source
                                        src={getOptimizedUrl(video)}
                                        type="video/mp4"
                                      />
                                      {t("global:videoSupport")}
                                    </video>
                                  ) : (
                                    <img src={uploadVideoImg} alt="" />
                                  )}
                                  <WrapperButton>
                                    <Button type="quaternary" icon="upload">
                                      {t("opportunity:media.selectVideo")}
                                    </Button>
                                  </WrapperButton>
                                </VideoUploadContainer>
                              )}
                            </Upload>
                          }
                        />
                        {video && (
                          <Popconfirm
                            placement="topLeft"
                            title={t("global:areYouSure")}
                            onConfirm={() => {
                              setVideo(null);
                              form.setFieldsValue({
                                video: ""
                              });
                              notification.warning({
                                message: t("global:alert"),
                                description: t("global:rememberSave")
                              });
                            }}
                            okText={t("global:yes")}
                            cancelText={t("global:no")}
                          >
                            <AntButton
                              style={{
                                marginLeft: 10,
                                position: "absolute",
                                top: 61,
                                left: 185
                              }}
                              type="default"
                              icon="delete"
                              sharp="circle"
                            />
                          </Popconfirm>
                        )}
                        <div style={{ marginLeft: 20 }}>
                          <strong>{t("global:description")}</strong>
                          <Description
                            style={{
                              textAlign: "left",
                              marginTop: 10
                            }}
                          >
                            {opportunity.description}
                          </Description>
                          <Gradient />
                        </div>
                      </div>
                    </ContentBottom>
                    <CompanyInfo>
                      <CustomAvatar
                        shape="square"
                        size={80}
                        src={getOptimizedImage(company.logo)}
                      />
                      {company.description && company.description.length > 0 && (
                        <>
                          <CompanyName>
                            {t("global:companyDescription")}
                          </CompanyName>
                          <CompanyDescriptionWrapper>
                            <Description rows={3}>
                              {company.description}
                            </Description>
                          </CompanyDescriptionWrapper>
                        </>
                      )}
                    </CompanyInfo>
                  </div>
                </div>
              </div>
            </BrowserLayout>
          </ContentForm>
          {!isDisabled && newOpportunity && (
            <Row
              type="flex"
              justify={newOpportunity ? "space-between" : "end"}
              style={{ marginTop: 40 }}
            >
              {newOpportunity && (
                <Button
                  type="quaternary"
                  disabled={loading}
                  size="lg"
                  onClick={goToPreviousStep}
                >
                  {t("global:back")}
                </Button>
              )}
              <Button
                type="primary"
                size="lg"
                htmlType="submit"
                loading={loading}
              >
                {newOpportunity ? t("global:next") : t("global:save")}
              </Button>
            </Row>
          )}
        </Form>
      </Container>
    </Content>
  );
};

const enhancer = compose(
  withStateHandlers(({ opportunity: { image, video } }) => ({ image, video }), {
    setImage: () => image => ({ image }),
    setVideo: () => video => ({ video })
  }),
  connect(
    state => ({
      newOpportunity: state.opportunity.newOpportunity,
      opportunity: state.opportunity.selected,
      company: state.company
    }),
    {
      saveOpportunityMedia,
      goToPreviousStep: GO_TO_PREVIOUS_WIZARD_STEP.run
    }
  ),
  isLoading(SAVE_OPPORTUNITY_MEDIA),
  withForm({
    selector: (state, { opportunity }) => R.pick(FORMS_KEYS.media, opportunity),
    onSubmit: (values, { saveOpportunityMedia, opportunity }) => {
      gtag.event({
        category: "VacancyDetail",
        action: "VacancyDetail.Media-Save",
        label: opportunity.id
      });
      saveOpportunityMedia({ id: opportunity.id, formData: values });
    }
  })
);

export default enhancer(MediaForm);
